import { readPremiumRefusal } from '../../../@commons/@services/front/premium-error.util';
import { PremiumTeaserComponent } from '../../../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../@commons/@toasts/premium-toast/premium-toast.component';
import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { localizedZoneName } from '../../../@commons/pipes/zone-name.pipe';
import { PageHeaderComponent } from '../../../@commons/@components/app-header/page-header/page-header.component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { GardenBedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/garden-bed/garden-bed-api';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { GardenBedType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-bed-type.enum';
import { LocationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/location-type.enum';
import { SunchineLevels } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/sunchine-levels.enum';
import { Orientation } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/orientation.enum';
import { SoilType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/soil-type.enum';
import { ChoiceOption } from '../../../@commons/ui-components/choice-selector/choice-selector.component';
import { BedCellsIconComponent } from '../../../@icons/bed-cells-icon.component';
import { PotIconComponent } from '../../../@icons/pot-icon.component';
import { GardenSpaceErrorCode } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-space-error-code.enum';
import {
  DEFAULT_CELL_CM,
  MAX_CELLS_PER_SIDE,
  formatMetres,
  gardenSpaceErrorKey,
} from '../../@services/garden-space.util';

interface Option {
  label: string;
  value: string;
  type?: LocationType;
}

/** Plus petite dimension acceptée par le back, en cm. */
const MIN_SIDE_CM = 10;
/** Au-delà, on n'atteint plus le milieu depuis le bord (revue UX L1-08). */
const REACH_CM = 150;
/** Un nombre plus petit est lu en mètres : « 1,2 » donne 120 cm. */
const METRES_BELOW = 10;
/** Tailles de case proposées dans les réglages d'une planche (de 20 à 60 cm). */
const CELL_SIZES = [20, 30, 40, 50, 60];
/** Pas de plan dans ces zones : on y cultive plutôt en pot ou en jardinière. */
const POT_ZONES: string[] = [LocationType.BALCONY, LocationType.TERRACE, LocationType.INDOOR];

/**
 * Créer ou modifier une planche (proposition du 28/10, écran 05). Une planche se cultive
 * en cases (bac, carré : plusieurs plants par case de 30 cm) ou c'est un pot (des places,
 * sans plan). Les rangs viendront avec le lot 2.
 *
 * En cases, on donne largeur et longueur en cm ; le back en déduit la grille. Le choix est
 * prérempli selon la zone : pot sur un balcon, une terrasse ou à l'intérieur.
 *
 * Modifier (route `beds/:bedUid/edit`) : une planche sans dimensions se présente en cases
 * de 30 cm (colonnes × 30, lignes × 30) ; enregistrer les confirme. La taille des cases
 * se règle ici. Changer cases ou pot n'est possible que planche vide (409 `BED_NOT_EMPTY`).
 */
@Component({
  selector: 'vw-garden-bed-create',
  imports: [
    PremiumTeaserComponent,
    PremiumToastComponent,
    SharedModule,
    FormsModule,
    TranslatePipe,
    PageHeaderComponent
],
  templateUrl: './garden-bed-create.component.html',
  styleUrl: './garden-bed-create.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: GardenBedApi, useClass: GardenBedApi },
    { provide: LocationApi, useClass: LocationApi },
  ],
})
export class GardenBedCreateComponent implements OnInit {
  readonly GardenBedType = GardenBedType;

  // Réutilisation inline (ex. wizard de création de zone) : zone imposée + pas de navigation.
  @Input() locationUid?: string;
  @Input() autoNavigate = true;
  /** Libellé du retour en usage intégré (« Retour aux planches ») ; sinon « Retour ». */
  @Input() backLabel?: string | null;
  @Output() bedCreated = new EventEmitter<any>();
  /** Usage intégré : « Retour » rend la main au parcours parent au lieu de quitter la page. */
  @Output() cancelled = new EventEmitter<void>();

  /** Planche modifiée ; absente, on en crée une. */
  bedUid?: string;
  private originalType?: GardenBedType;
  /** Ancienne planche sans dimensions : 30 cm supposés, à confirmer. */
  dimensionsAssumed = false;
  /** Plantations de la planche modifiée : la nouvelle taille ne doit couper personne. */
  private plantedExtent: { columns: number; rows: number; name: string } | null = null;
  /** Le nom a été tapé : on ne le remplace plus par « Planche 2 ». */
  private nameTouched = false;

  // Form state
  name = '';
  type: GardenBedType = GardenBedType.RECTANGULAR;
  /** Le jardinier a choisi lui-même : changer de zone ne le change plus. */
  private typeChosen = false;
  widthCm: string | number = '120';
  lengthCm: string | number = '90';
  cellCm = DEFAULT_CELL_CM;
  capacity = 3;
  exposure?: SunchineLevels;
  orientation?: Orientation;
  soilType?: SoilType;

  // Zone : soit imposée (header) si passée en query param, soit choisie (dropdown).
  zone?: Option;
  zones: Option[] = [];
  presetZoneName?: string;
  private presetZoneUid?: string;

  readonly typeOptions: ChoiceOption[] = [
    {
      value: GardenBedType.RECTANGULAR,
      label: 'garden.beds.create.kind.cells',
      description: 'garden.beds.create.kind.cells-hint',
      iconComponent: BedCellsIconComponent,
    },
    {
      value: GardenBedType.POT,
      label: 'garden.beds.create.kind.pot',
      description: 'garden.beds.create.kind.pot-hint',
      iconComponent: PotIconComponent,
    },
  ];
  readonly cellSizePills: ChoiceOption[] = CELL_SIZES.map((size) => ({ value: size, label: `${size} cm` }));

  // Pills (réutilisent le composant générique vw-choice-selector)
  orientationPills: ChoiceOption[] = [];
  exposurePills: ChoiceOption[] = [];
  soilPills: ChoiceOption[] = [];

  submitLoading = false;
  nameError = false;
  /**
   * « Créer » touché alors qu'il manque quelque chose : le bouton n'est jamais grisé, il dit
   * quoi (au-dessus du bouton) et amène au champ. Le message s'efface dès que tout est bon.
   */
  submitAttempted = false;
  /** Refus de l'espace du potager (planche occupée…) : on dit pourquoi. */
  errorKey: string | null = null;
  /**
   * Trop de cases pour la taille (`GRID_TOO_LARGE`) : la case du back qui tiendrait, ou `null`
   * au-delà de 60 cm (il faudra des rangs, lot 2).
   */
  serverSuggestion: { cellCm: number | null } | null = null;
  loadError = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private gardenBedApi: GardenBedApi,
    private locationApi: LocationApi,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.orientationPills = Object.values(Orientation).map((v) => ({
      value: v,
      label: `garden.beds.enums.orientation.${v}`,
    }));
    this.exposurePills = Object.values(SunchineLevels).map((v) => ({
      value: v,
      label: `garden.beds.enums.exposure.${v}`,
    }));
    this.soilPills = Object.values(SoilType).map((v) => ({
      value: v,
      label: `garden.beds.enums.soil.${v}`,
    }));
    // Seule la page routée modifie : en usage inline, la route est celle de l'écran hôte.
    this.bedUid = this.autoNavigate ? this.route.snapshot?.paramMap?.get('bedUid') ?? undefined : undefined;
    if (this.bedUid) {
      await this.loadBed(this.bedUid);
    }
    // L'@Input a priorité (usage inline), le query param reste le fallback (route /garden/beds/create).
    this.presetZoneUid =
      this.presetZoneUid ??
      this.locationUid ??
      this.route.snapshot?.queryParamMap?.get('locationUid') ??
      undefined;
    await this.loadZones();
  }

  get editing(): boolean {
    return !!this.bedUid;
  }

  /** Planche à modifier : ses réglages, et ses dimensions supposées si elle n'en a pas. */
  private async loadBed(uid: string): Promise<void> {
    try {
      const bed: any = await this.gardenBedApi.find(uid, this.storageService.getCurrentLanguage());
      this.name = bed?.name ?? '';
      this.type = bed?.type ?? GardenBedType.RECTANGULAR;
      this.originalType = this.type;
      this.typeChosen = true;
      this.exposure = bed?.exposure ?? undefined;
      this.orientation = bed?.orientation ?? undefined;
      this.soilType = bed?.soilType ?? undefined;
      this.capacity = bed?.capacity ?? this.capacity;
      this.cellCm = bed?.cellCm ?? DEFAULT_CELL_CM;
      this.dimensionsAssumed = this.type === GardenBedType.RECTANGULAR && (bed?.widthCm == null || bed?.lengthCm == null);
      this.widthCm = bed?.widthCm ?? (bed?.columns ?? 1) * this.cellCm;
      this.lengthCm = bed?.lengthCm ?? (bed?.rows ?? 1) * this.cellCm;
      this.presetZoneUid = bed?.location?.uid ?? bed?.locationUid ?? undefined;
      this.nameTouched = true;
      if (this.type === GardenBedType.RECTANGULAR) await this.loadPlantedExtent(uid);
    } catch (error) {
      console.warn('GardenBedCreateComponent: failed to load the bed', error);
      this.loadError = true;
    }
  }

  /** Jusqu'où vont les plantations de la planche, en cases. */
  private async loadPlantedExtent(uid: string): Promise<void> {
    try {
      const grid = await this.gardenBedApi.getGrid(uid, this.storageService.getCurrentLanguage());
      // Jusqu'où vont les plantations ; le nom est celui de la plantation qui va le plus loin.
      let extent: { columns: number; rows: number; name: string } | null = null;
      let reach = 0;
      for (const c of grid?.cells ?? []) {
        // Le plan peut renvoyer un nom vide : le message dit alors « Une plantation ».
        if (c.positionX == null || c.positionY == null) continue;
        const columns = c.positionX + (c.spanX || 1);
        const rows = c.positionY + (c.spanY || 1);
        const name = c.cultivationName?.trim() ?? '';
        const next = { columns: Math.max(columns, extent?.columns ?? 0), rows: Math.max(rows, extent?.rows ?? 0), name: extent?.name ?? name };
        if (columns + rows > reach) {
          reach = columns + rows;
          next.name = name;
        }
        extent = next;
      }
      this.plantedExtent = extent;
    } catch (error) {
      // Sans plan, on ne peut pas prévenir : le back garde les plantations où elles sont.
      console.warn('GardenBedCreateComponent: failed to load the bed plan', error);
    }
  }

  async loadZones(): Promise<void> {
    const addressUid = this.storageService.getItem('address-uid') ?? undefined;
    const res = await this.locationApi.list(
      { addressUid } as any,
      0,
      50,
      this.storageService.getCurrentLanguage(),
    );
    // Une zone d'office porte le nom de son type (« PLANTER_BOX ») : on le traduit.
    this.zones = res.results.map((l) => ({ label: localizedZoneName(l, this.translate), value: l.uid, type: l.locationType }));
    if (this.presetZoneUid) {
      this.zone = this.zones.find((z) => z.value === this.presetZoneUid);
      this.presetZoneName = this.zone?.label;
    }
    this.suggestType();
    await this.suggestName();
  }

  /** Nom prérempli (« Planche 2 ») : le numéro suit les planches de la zone. */
  async suggestName(): Promise<void> {
    if (this.nameTouched || this.editing) return;
    const zoneUid = this.presetZoneUid ?? this.zone?.value;
    let count = 0;
    try {
      const beds = await this.gardenBedApi.list({}, 0, 200, this.storageService.getCurrentLanguage());
      count = (beds?.results ?? []).filter((b: any) => !zoneUid || b.locationUid === zoneUid).length;
    } catch {
      // Sans la liste, « Planche 1 » : le nom reste modifiable.
    }
    this.defaultIndex = count + 1;
    this.name = this.defaultName();
  }

  private defaultIndex = 1;

  private defaultName(): string {
    const key = this.isRectangular ? 'garden.beds.create.default-name' : 'garden.beds.create.default-name-pot';
    return this.translate.instant(key, { n: this.defaultIndex });
  }

  onNameChange(): void {
    this.nameTouched = true;
  }

  /** Pot sur un balcon, une terrasse ou à l'intérieur ; en cases ailleurs. */
  suggestType(): void {
    if (this.typeChosen || !this.zone?.type) return;
    this.type = POT_ZONES.includes(this.zone.type) ? GardenBedType.POT : GardenBedType.RECTANGULAR;
  }

  onZoneChange(): void {
    this.suggestType();
    void this.suggestName();
  }

  get isRectangular(): boolean {
    return this.type === GardenBedType.RECTANGULAR;
  }

  selectType(type: GardenBedType): void {
    if (!type) return;
    this.type = type;
    this.typeChosen = true;
    if (!this.nameTouched && !this.editing) this.name = this.defaultName();
  }

  // --- Dimensions -------------------------------------------------------

  get width(): number {
    return toCm(this.widthCm);
  }

  get length(): number {
    return toCm(this.lengthCm);
  }

  /** Unité affichée à côté d'un champ : « m » quand le nombre tapé se lit en mètres (« 1,2 »). */
  unitOf(value: string | number): string {
    const n = Number(String(value ?? '').trim().replace(',', '.'));
    return n > 0 && n < METRES_BELOW ? 'm' : 'cm';
  }

  /** Les deux côtés dépassent 1,50 m : le milieu ne s'atteint plus depuis le bord. */
  get tooWide(): boolean {
    return this.isRectangular && this.dimensionsValid && this.width > REACH_CM && this.length > REACH_CM;
  }

  /** La nouvelle taille couperait une plantation : « Oignon dépasse, garde au moins 1,20 × 0,90 m. » */
  get cutsPlanting(): { name: string; width: string; length: string } | null {
    const e = this.plantedExtent;
    if (!e || !this.isRectangular || (this.columns >= e.columns && this.rows >= e.rows)) return null;
    const lang = this.storageService.getCurrentLanguage();
    return {
      name: e.name,
      width: formatMetres(Math.max(this.width, e.columns * this.cellCm), lang),
      length: formatMetres(Math.max(this.length, e.rows * this.cellCm), lang),
    };
  }

  /** Plus grand côté : 20 cases. */
  get maxSideCm(): number {
    return MAX_CELLS_PER_SIDE * this.cellCm;
  }

  private sideValid(cm: number): boolean {
    return Number.isInteger(cm) && cm >= MIN_SIDE_CM && cm <= this.maxSideCm;
  }

  /** Un côté dépasse 20 cases mais tiendrait avec une plus grande case (pré-contrôle du web). */
  get tooManyCells(): boolean {
    const side = Math.max(this.width, this.length);
    return this.isRectangular && side > this.maxSideCm;
  }

  /**
   * « Avec des cases de 50 cm, ça tient » : la réponse du back si elle est là, sinon la plus
   * petite case proposée qui tient ; `cellCm: null` si même 60 cm ne suffit pas.
   */
  get gridSuggestion(): { cellCm: number | null } | null {
    if (this.serverSuggestion) return this.serverSuggestion;
    if (!this.tooManyCells) return null;
    const side = Math.max(this.width, this.length);
    const size = CELL_SIZES.find((s) => s > this.cellCm && side <= MAX_CELLS_PER_SIDE * s);
    return { cellCm: size ?? null };
  }

  useCellSize(size: number): void {
    this.cellCm = size;
    this.serverSuggestion = null;
    this.errorKey = null;
  }

  get widthError(): boolean {
    return this.isRectangular && !this.sideValid(this.width);
  }

  get lengthError(): boolean {
    return this.isRectangular && !this.sideValid(this.length);
  }

  get dimensionsValid(): boolean {
    return !this.widthError && !this.lengthError;
  }

  /** Colonnes et lignes que le back déduira : une case entière au moins. */
  get columns(): number {
    return Math.max(1, Math.floor(this.width / this.cellCm));
  }

  get rows(): number {
    return Math.max(1, Math.floor(this.length / this.cellCm));
  }

  get gridCellCount(): number {
    return this.columns * this.rows;
  }

  get widthLabel(): string {
    return formatMetres(this.width, this.storageService.getCurrentLanguage());
  }

  get lengthLabel(): string {
    return formatMetres(this.length, this.storageService.getCurrentLanguage());
  }

  // Tableau pour l'aperçu (en cases = colonnes × lignes, pot = places).
  get previewCells(): number[] {
    const n = this.isRectangular ? this.gridCellCount : this.capacity || 0;
    return Array.from({ length: Math.min(n, MAX_CELLS_PER_SIDE * MAX_CELLS_PER_SIDE) });
  }

  /** Ce qui manque pour créer, dans l'ordre du formulaire ; `null` si tout est bon. */
  get missingKey(): string | null {
    if (!this.name?.trim()) return 'garden.beds.create.missing.name';
    if (!this.zone && !this.presetZoneUid) return 'garden.beds.create.missing.zone';
    if (this.isRectangular && (!this.dimensionsValid || this.cutsPlanting)) return 'garden.beds.create.missing.dimensions';
    return null;
  }

  /** Le premier champ à reprendre, pour y amener le doigt (et le clavier). */
  private focusFirstMissing(): void {
    const id = !this.name?.trim() ? 'bed-name' : this.widthError || this.cutsPlanting ? 'bed-width' : this.lengthError ? 'bed-length' : null;
    if (id) document.getElementById(id)?.focus();
  }

  get canSubmit(): boolean {
    return (
      !!this.name?.trim() &&
      (!!this.zone || !!this.presetZoneUid) &&
      (!this.isRectangular || (this.dimensionsValid && !this.cutsPlanting))
    );
  }

  private payload(): any {
    const common = {
      name: this.name.trim(),
      exposure: this.exposure,
      orientation: this.orientation,
      soilType: this.soilType,
    };
    if (this.isRectangular) {
      return {
        ...common,
        widthCm: this.width,
        lengthCm: this.length,
        cellCm: this.cellCm,
      };
    }
    return { ...common, capacity: Number(this.capacity) };
  }

  async submit(): Promise<void> {
    this.nameError = !this.name?.trim();
    if (this.submitLoading) return;
    if (!this.canSubmit) {
      this.submitAttempted = true;
      this.focusFirstMissing();
      return;
    }
    this.submitLoading = true;
    this.errorKey = null;
    this.serverSuggestion = null;
    try {
      if (this.editing) {
        await this.gardenBedApi.update(
          {
            uid: this.bedUid as string,
            ...this.payload(),
            ...(this.type !== this.originalType ? { type: this.type } : {}),
          },
          this.storageService.getCurrentLanguage(),
        );
        this.router.navigate(['/garden/beds', this.bedUid]);
        return;
      }
      const created = await this.gardenBedApi.create({
        locationUid: this.presetZoneUid ?? this.zone!.value,
        type: this.type,
        ...this.payload(),
      });
      this.bedCreated.emit(created);
      if (this.autoNavigate) {
        this.router.navigate(['/garden/beds', created.uid]);
      }
    } catch (error) {
      // Un refus premium n'est jamais une erreur rouge : on explique et on
      // propose. Un refus de place se dit ; toute autre erreur suit le chemin habituel.
      const refusal = readPremiumRefusal(error);
      const spaceKey = gardenSpaceErrorKey(error);
      const data = (error as { data?: { code?: string; suggestedCellCm?: number | null } })?.data;
      if (refusal) {
        this.premiumMessageKey = refusal.messageKey ?? 'premium.gate.bed';
      } else if (data?.code === GardenSpaceErrorCode.GRID_TOO_LARGE) {
        this.serverSuggestion = { cellCm: data.suggestedCellCm ?? null };
      } else if (spaceKey) {
        this.errorKey = spaceKey;
      } else {
        throw error;
      }
    } finally {
      this.submitLoading = false;
    }
  }

  /** Message d'invitation premium, quand la limite gratuite est atteinte. */
  premiumMessageKey: string | null = null;
  premiumPaywallOpen = false;

  openPaywall(): void {
    this.premiumPaywallOpen = true;
  }

  back(): void {
    if (!this.autoNavigate) {
      this.cancelled.emit();
      return;
    }
    if (this.editing) {
      this.router.navigate(['/garden/beds', this.bedUid]);
      return;
    }
    this.router.navigate(['/garden/list']);
  }
}

/** Centimètres d'une saisie : « 120 », « 1,2 » ou « 1.20 » (mètres sous 10). */
export function toCm(value: string | number | null | undefined): number {
  const n = Number(String(value ?? '').trim().replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < METRES_BELOW ? Math.round(n * 100) : n;
}
